import "../css/header.scss";
import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiShoppingBag } from "react-icons/fi";
import { CartContext } from "./CartContext";
import { siteConfig } from "../config/siteConfig";

const primaryNavigation = [
  { label: "Shop", to: "/shop" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const informationNavigation = [
  { label: "FAQs", to: "/faqs" },
  { label: "Shipping", to: "/shipping" },
  { label: "Returns", to: "/returns" },
  { label: "Privacy", to: "/privacy" },
  { label: "Cookies", to: "/cookies" },
  { label: "Terms", to: "/terms" },
];

function Header() {
  const { cartQuantity } = useContext(CartContext);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = location.pathname === "/";
  const isShop = location.pathname === "/shop";

  useEffect(() => {
    setMenuOpen(false);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, location.pathname]);

  useEffect(() => {
    const titles = {
      "/": "Home and lifestyle collection",
      "/shop": "Shop",
      "/cartpage": "Basket",
      "/checkout": "Checkout",
      "/paysuccess": "Order complete",
      "/about": "About",
      "/contact": "Contact",
      "/privacy": "Privacy policy",
      "/terms": "Terms of service",
      "/returns": "Returns",
      "/shipping": "Shipping",
      "/faqs": "FAQs",
      "/cookies": "Cookie policy",
      "/payment": "Payment status",
    };
    const pageTitle = location.pathname.startsWith("/shopDetails/")
      ? "Object"
      : titles[location.pathname] || "Home and lifestyle collection";
    document.title = `${pageTitle} — ${siteConfig.brandName}`;
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle("menu-is-open", menuOpen);
    return () => document.body.classList.remove("menu-is-open");
  }, [menuOpen]);

  const navClassName = (item) => {
    const [pathname, hash = ""] = item.to.split("#");
    const isActive = hash
      ? location.pathname === pathname && location.hash === `#${hash}`
      : location.pathname === pathname && !location.hash;
    return `site-header__nav-link${isActive ? " is-active" : ""}`;
  };

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header
        className={`site-header${isHome ? " site-header--home" : ""}${
          isShop ? " site-header--shop" : ""
        }`}
      >
        <div className="site-header__inner">
          <button
            className="site-header__mobile-trigger"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span>{menuOpen ? "Close" : "Menu"}</span>
          </button>

          <nav className="site-header__nav" aria-label="Primary navigation">
            {primaryNavigation.map((item) => (
              <Link key={item.label} className={navClassName(item)} to={item.to}>
                {item.label}
              </Link>
            ))}
            <details className="site-header__more">
              <summary>More</summary>
              <div>
                {informationNavigation.map((item) => (
                  <Link key={item.label} to={item.to}>{item.label}</Link>
                ))}
              </div>
            </details>
          </nav>

          <Link className="site-header__logo" to="/" aria-label={`${siteConfig.brandName} home`}>
            {siteConfig.brandName}
          </Link>

          <div className="site-header__actions">
            <Link
              className="site-header__action"
              to="/cartpage"
              aria-label={`Shopping bag with ${cartQuantity} items`}
            >
              <FiShoppingBag aria-hidden="true" />
              <span className="sr-only">{cartQuantity} items</span>
            </Link>
          </div>
        </div>
      </header>

      <nav
        id="mobile-navigation"
        className={`mobile-navigation${menuOpen ? " is-open" : ""}`}
        aria-label="Mobile navigation"
        aria-hidden={!menuOpen}
      >
        <div className="mobile-navigation__primary">
          {primaryNavigation.map((item, index) => (
            <Link key={item.label} to={item.to} tabIndex={menuOpen ? 0 : -1}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              {item.label}
            </Link>
          ))}
        </div>
        <div className="mobile-navigation__secondary">
          <Link to="/contact" tabIndex={menuOpen ? 0 : -1}>Contact</Link>
          <Link to="/shipping" tabIndex={menuOpen ? 0 : -1}>Shipping</Link>
          <Link to="/returns" tabIndex={menuOpen ? 0 : -1}>Returns</Link>
          <Link to="/privacy" tabIndex={menuOpen ? 0 : -1}>Privacy</Link>
          <Link to="/terms" tabIndex={menuOpen ? 0 : -1}>Terms</Link>
          <Link to="/faqs" tabIndex={menuOpen ? 0 : -1}>FAQs</Link>
          <Link to="/cookies" tabIndex={menuOpen ? 0 : -1}>Cookies</Link>
        </div>
        <p>{siteConfig.tagline}</p>
      </nav>
    </>
  );
}

export default Header;
